import { createRouter, createWebHistory } from 'vue-router'
import { watch } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useAuth, ROLE_ADMIN, ROLE_INVESTOR } from './composables/useAuth'
import Positions from './views/Positions.vue'
import TradeHistory from './views/TradeHistory.vue'
import PostMortem from './views/PostMortem.vue'
import OrderHistory from './views/OrderHistory.vue'
import Watchlist from './views/Watchlist.vue'
import WatchlistHistory from './views/WatchlistHistory.vue'
import AdminLayout from './layouts/AdminLayout.vue'
import SettingsView from './views/SettingsView.vue'
import KillSwitchView from './views/KillSwitchView.vue'
import BacktestView from './views/BacktestView.vue'
import AlpacaImportView from './views/AlpacaImportView.vue'
import Forbidden from './views/Forbidden.vue'

const GUARD_PREFIX = '[RouterGuard]'

const routes = [
  {
    path: '/',
    name: 'Positions',
    component: Positions,
    meta: { requiresAuth: true, requiresRole: ROLE_INVESTOR }
  },
  {
    path: '/trade-history',
    name: 'TradeHistory',
    component: TradeHistory,
    meta: { requiresAuth: true, requiresRole: ROLE_INVESTOR }
  },
  {
    path: '/post-mortem',
    name: 'PostMortem',
    component: PostMortem,
    meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
  },
  {
    path: '/orders',
    name: 'OrderHistory',
    component: OrderHistory,
    meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
  },
  {
    path: '/watchlist',
    name: 'Watchlist',
    component: Watchlist,
    meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
  },
  {
    path: '/watchlist/history',
    name: 'WatchlistHistory',
    component: WatchlistHistory,
    meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresRole: ROLE_ADMIN },
    children: [
      {
        path: 'settings',
        name: 'AdminSettings',
        component: SettingsView,
        meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
      },
      {
        path: 'kill-switch',
        name: 'KillSwitch',
        component: KillSwitchView,
        meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
      },
      {
        path: 'backtest',
        name: 'Backtest',
        component: BacktestView,
        meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
      },
      {
        path: 'alpaca-import',
        name: 'AlpacaImport',
        component: AlpacaImportView,
        meta: { requiresAuth: true, requiresRole: ROLE_ADMIN }
      }
    ]
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: Forbidden,
    meta: { requiresAuth: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some(r => r.meta.requiresAuth)

  if (!requiresAuth) {
    console.log(`${GUARD_PREFIX} ${String(to.name)} → allowed (no auth required)`)
    return true
  }

  // Capture all reactive refs from inject()-based composables BEFORE any
  // await. Vue's app inject context is only active synchronously inside the
  // guard; after the first await it's gone, so a later useAuth0() / useAuth()
  // call returns undefined and crashes the destructure.
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const { isAdmin, hasAnyRole } = useAuth()

  // Wait for Auth0 to finish its initial loading before deciding
  if (isLoading.value) {
    await new Promise(resolve => {
      const unwatch = watch(isLoading, loading => {
        if (!loading) {
          unwatch()
          resolve()
        }
      })
    })
  }

  if (!isAuthenticated.value) {
    console.log(`${GUARD_PREFIX} ${String(to.name)} → redirected to login`)
    loginWithRedirect({ appState: { target: to.fullPath } })
    return false
  }

  // Role gate. The deepest matched route's `requiresRole` wins; if absent
  // (e.g. /forbidden) we allow any authenticated user through.
  const requiredRole = [...to.matched].reverse().find(r => r.meta.requiresRole)?.meta.requiresRole
  if (requiredRole) {
    const allowed = requiredRole === ROLE_ADMIN ? isAdmin.value : hasAnyRole.value
    if (!allowed) {
      console.log(`${GUARD_PREFIX} ${String(to.name)} → denied (requires role: ${requiredRole})`)
      return { name: 'Forbidden' }
    }
  }

  console.log(`${GUARD_PREFIX} ${String(to.name)} → allowed`)
  return true
})

export default router
